import React, { Suspense, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import {
  APP_NAV_ITEMS,
  canAccessNavItem,
  getAccessibleNavItems,
} from '../navigation/appNavigation';
import PrivateRoute from './PrivateRoute';
import AppShell from '../components/AppShell';

const LoginPage = React.lazy(() => import('../pages/LoginPage'));
const ForgotPasswordPage = React.lazy(() => import('../pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('../pages/ResetPasswordPage'));
const VerifyEmailPage = React.lazy(() => import('../pages/VerifyEmailPage'));
const DashboardPage = React.lazy(() => import('../pages/DashboardPage'));
const OauthClientsPage = React.lazy(() => import('../pages/OauthClientsPage'));
const OauthUsersPage = React.lazy(() => import('../pages/OauthUsersPage'));
// MedTransact business modules.
const ProviderPage = React.lazy(() => import('../pages/ProviderPage'));
const DiagnosisPage = React.lazy(() => import('../pages/DiagnosisPage'));
const CptPage = React.lazy(() => import('../pages/CptPage'));
const PhysicianPage = React.lazy(() => import('../pages/PhysicianPage'));
const CorporatePage = React.lazy(() => import('../pages/CorporatePage'));
const PlanSetupPage = React.lazy(() => import('../pages/PlanSetupPage'));
const MemberPage = React.lazy(() => import('../pages/MemberPage'));
const PaymentsPage = React.lazy(() => import('../pages/PaymentsPage'));
const MisReportPage = React.lazy(() => import('../pages/MisReportPage'));
const UploadS3PublicShareTokenPage = React.lazy(() => import('../pages/UploadS3PublicShareTokenPage'));
const AuditLogsPage = React.lazy(() => import('../pages/AuditLogsPage'));
const OutboundHttpLogsPage = React.lazy(() => import('../pages/OutboundHttpLogsPage'));
const FieldHistoryPage = React.lazy(() => import('../pages/FieldHistoryPage'));
const LoginAttemptsPage = React.lazy(() => import('../pages/LoginAttemptsPage'));
const WebhookDeliveriesPage = React.lazy(() => import('../pages/WebhookDeliveriesPage'));
const FailedJobsPage = React.lazy(() => import('../pages/FailedJobsPage'));
const SystemSettingsPage = React.lazy(() => import('../pages/SystemSettingsPage'));
const ProfilePage = React.lazy(() => import('../pages/ProfilePage'));
const SessionsPage = React.lazy(() => import('../pages/SessionsPage'));
const ChangePasswordPage = React.lazy(() => import('../pages/ChangePasswordPage'));
const MfaSettingsPage = React.lazy(() => import('../pages/MfaSettingsPage'));
// Rule 73 — Authorized Device account-security landing.
const AccountSecurityPage = React.lazy(() => import('../pages/AccountSecurityPage'));
// Rule 70 — Federated identity (Sign in with Google / Facebook).
const OAuthCallbackPage = React.lazy(() => import('../pages/OAuthCallbackPage'));
const CompleteProfilePage = React.lazy(() => import('../pages/CompleteProfilePage'));
const LinkedAccountsPage = React.lazy(() => import('../pages/LinkedAccountsPage'));
// NOTE: Bloom / Chat / Voice-Video Call / Example-Batch modules moved to
// `backup-not-used/` (not used by MedTransact). Restore from there if needed.
// const ModulePage = React.lazy(() => import('../pages/ModulePage'));

function RouteFallback() {
  const { t } = useTranslation();

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex h-full min-h-[12rem] items-center justify-center text-sm text-gray-500"
    >
      {t('app.loading')}
    </div>
  );
}

function withRouteSuspense(element) {
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

function renderModuleRoute(item) {
  if (item.path === '/dashboard') return withRouteSuspense(<DashboardPage />);
  if (item.path === '/medical/provider') return withRouteSuspense(<ProviderPage />);
  if (item.path === '/medical/diagnosis') return withRouteSuspense(<DiagnosisPage />);
  if (item.path === '/medical/cpt') return withRouteSuspense(<CptPage />);
  if (item.path === '/medical/physician') return withRouteSuspense(<PhysicianPage />);
  if (item.path === '/corporate/accounts') return withRouteSuspense(<CorporatePage />);
  if (item.path === '/corporate/plan-setup') return withRouteSuspense(<PlanSetupPage />);
  if (item.path === '/member') return withRouteSuspense(<MemberPage />);
  if (item.path === '/payments') return withRouteSuspense(<PaymentsPage />);
  if (item.path === '/mis-report') return withRouteSuspense(<MisReportPage />);
  if (item.path === '/admin/users') return withRouteSuspense(<OauthUsersPage />);
  if (item.path === '/admin/oauth-clients') return withRouteSuspense(<OauthClientsPage />);
  if (item.path === '/admin/upload-s3-public-share-tokens') {
    return withRouteSuspense(<UploadS3PublicShareTokenPage />);
  }
  if (item.path === '/admin/audit-logs') return withRouteSuspense(<AuditLogsPage />);
  if (item.path === '/admin/outbound-http-logs') return withRouteSuspense(<OutboundHttpLogsPage />);
  if (item.path === '/admin/field-history') return withRouteSuspense(<FieldHistoryPage />);
  if (item.path === '/admin/login-attempts') return withRouteSuspense(<LoginAttemptsPage />);
  if (item.path === '/admin/webhook-deliveries') return withRouteSuspense(<WebhookDeliveriesPage />);
  if (item.path === '/admin/failed-jobs') return withRouteSuspense(<FailedJobsPage />);
  if (item.path === '/admin/system-settings') return withRouteSuspense(<SystemSettingsPage />);
  // if (item.path === '/{path}') return withRouteSuspense(<ModulePage />);

  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-400">
      <p className="text-lg font-semibold">{item.label}</p>
      <p className="text-sm">{item.description}</p>
    </div>
  );
}

export default function AppRoutes() {
  const { isAuthenticated, user } = useAuth();
  const userScopes = useMemo(
    () => (Array.isArray(user?.scopes) ? user.scopes : []),
    [user?.scopes],
  );
  const fallbackPath = useMemo(
    () => getAccessibleNavItems(userScopes)[0]?.path ?? '/dashboard',
    [userScopes],
  );

  return (
    <Routes>
      <Route
        path="/"
        element={<Navigate to={isAuthenticated ? fallbackPath : '/login'} replace />}
      />

      <Route path="/login" element={withRouteSuspense(<LoginPage />)} />
      <Route path="/forgot-password" element={withRouteSuspense(<ForgotPasswordPage />)} />
      <Route path="/reset-password" element={withRouteSuspense(<ResetPasswordPage />)} />
      <Route path="/verify-email" element={withRouteSuspense(<VerifyEmailPage />)} />
      {/* Rule 70 — federated callback. Public; the page reads the URL fragment
          and POSTs the one-shot handoff code to /auth/identities/exchange. */}
      <Route path="/oauth/callback" element={withRouteSuspense(<OAuthCallbackPage />)} />

      <Route element={<PrivateRoute />}>
        <Route element={<AppShell />}>
          {APP_NAV_ITEMS.map((item) => (
            <Route
              key={item.path}
              path={item.path}
              element={
                canAccessNavItem(userScopes, item)
                  ? renderModuleRoute(item)
                  : <Navigate to={fallbackPath} replace />
              }
            />
          ))}
          <Route path="/profile" element={withRouteSuspense(<ProfilePage />)} />
          <Route path="/profile/sessions" element={withRouteSuspense(<SessionsPage />)} />
          <Route path="/profile/change-password" element={withRouteSuspense(<ChangePasswordPage />)} />
          <Route path="/profile/mfa" element={withRouteSuspense(<MfaSettingsPage />)} />
          <Route path="/profile/account-security" element={withRouteSuspense(<AccountSecurityPage />)} />
          <Route path="/profile/linked-accounts" element={withRouteSuspense(<LinkedAccountsPage />)} />
          <Route path="/profile/complete" element={withRouteSuspense(<CompleteProfilePage />)} />
          {/* <Route path="/profile" element={withRouteSuspense(<ProfilePage />)} /> */}
        </Route>
      </Route>

      <Route
        path="*"
        element={<Navigate to={isAuthenticated ? fallbackPath : '/login'} replace />}
      />
    </Routes>
  );
}
